import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Plus, Paperclip, Banknote, Percent, CheckCircle2, AlertCircle } from 'lucide-react';
import dayjs from 'dayjs';
import { CreateHoFeesSharePayload, HoFeesShareApi, HoFeesShareRow } from '@/services/ho-fees-share.api';
import { SchoolsApi } from '@/services/superadmin.api';
import { OrgPortalApi } from '@/services/org.api';
import { UploadApi } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Modal } from '@/components/ui/Modal';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { fmtMoney } from './lib/feeFormat';

const columnHelper = createColumnHelper<HoFeesShareRow>();

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

/**
 * Head Office Fees Share — a HISTORICAL LEDGER + manual entry, not a live
 * calculator. The source data has no derivable percentage/commission
 * formula (confirmed against real fee-collection figures), so this only
 * records and displays what's manually entered — no auto-calculation.
 */
export function HoFeesSharePage({ scope }: { scope: 'superadmin' | 'organization' | 'school' }) {
  const qc = useQueryClient();
  const showSchoolPicker = scope !== 'school';
  const isSchoolScope = scope === 'school';

  const [addOpen, setAddOpen] = useState(false);
  const [schoolFilter, setSchoolFilter] = useState('');
  const [sessionYear, setSessionYear] = useState('');
  const dt = useDataTable({ defaultLimit: 20 });

  useEffect(() => {
    dt.onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolFilter, sessionYear]);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-ho-fees-share-filter', scope],
    queryFn: async () =>
      scope === 'organization' ? (await OrgPortalApi.listSchools({ limit: 100 })).items : (await SchoolsApi.list({ limit: 100 })).items,
    enabled: showSchoolPicker,
  });

  const { data: academicYears = [] } = useQuery({
    queryKey: ['ho-fees-share', 'academic-years'],
    queryFn: HoFeesShareApi.academicYears,
    enabled: isSchoolScope,
  });
  const effectiveSessionYear = sessionYear || academicYears.find((y) => y.isCurrent)?.name || '';

  const { data: summary } = useQuery({
    queryKey: ['ho-fees-share', 'summary', effectiveSessionYear],
    queryFn: () => HoFeesShareApi.summary(effectiveSessionYear),
    enabled: isSchoolScope && !!effectiveSessionYear,
  });

  const { data, isFetching } = useQuery({
    queryKey: ['ho-fees-share', { ...dt.params, schoolFilter, scope, effectiveSessionYear }],
    queryFn: () =>
      HoFeesShareApi.list({
        ...dt.params,
        schoolId: showSchoolPicker ? schoolFilter || undefined : undefined,
        sessionYear: isSchoolScope ? effectiveSessionYear || undefined : undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['ho-fees-share'] });
  };

  const columns = useMemo(
    () => [
      ...(showSchoolPicker ? [columnHelper.accessor('schoolName', { id: 'schoolName', header: 'School' })] : []),
      columnHelper.accessor('amount', { header: 'Amount', cell: (info) => fmtMoney(info.getValue()) }),
      columnHelper.accessor('date', { header: 'Date', cell: (info) => dayjs(info.getValue()).format('DD MMM YYYY') }),
      columnHelper.display({
        id: 'paymentMethod',
        header: 'Payment Method',
        cell: (info) => info.row.original.paymentMethod ?? '—',
      }),
      columnHelper.display({
        id: 'referenceNo',
        header: 'Reference #',
        cell: (info) => info.row.original.referenceNo ?? '—',
      }),
      columnHelper.display({
        id: 'sessionYear',
        header: 'Session Year',
        cell: (info) => info.row.original.sessionYear ?? '—',
      }),
      columnHelper.display({
        id: 'notes',
        header: 'Notes',
        cell: (info) => <span className="line-clamp-2 max-w-xs text-xs text-slate-600">{info.row.original.notes ?? '—'}</span>,
      }),
      columnHelper.display({
        id: 'attachment',
        header: '',
        cell: (info) => {
          const url = info.row.original.attachmentUrl;
          if (!url) return null;
          const isLink = /^https?:\/\//.test(url) || url.startsWith('/');
          return isLink ? (
            <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800" title="View attachment">
              <Paperclip className="h-4 w-4" />
            </a>
          ) : (
            <span className="text-xs text-slate-400" title={url}>
              <Paperclip className="h-4 w-4" />
            </span>
          );
        },
      }),
    ],
    [showSchoolPicker],
  );

  return (
    <>
      <PageHeader
        title="Head Office Fees Share"
        description="Historical ledger of Head Office's fees share per school — manual entries, no auto-calculation."
        actions={
          <div className="flex items-center gap-2">
            {isSchoolScope && academicYears.length > 0 && (
              <Select className="!w-36" value={effectiveSessionYear} onChange={(e) => setSessionYear(e.target.value)}>
                {academicYears.map((y) => (
                  <option key={y.id} value={y.name}>
                    {y.name}
                    {y.isCurrent ? ' (current)' : ''}
                  </option>
                ))}
              </Select>
            )}
            <button className="btn-primary" onClick={() => setAddOpen(true)}>
              <Plus className="mr-1.5 h-4 w-4" /> Add Head Office Payment
            </button>
          </div>
        }
      />

      {isSchoolScope && summary && (
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <HoSummaryCard icon={Banknote} tone="green" label="Total Fees" value={fmtMoney(summary.totalFees)} />
          <HoSummaryCard
            icon={Percent}
            tone="blue"
            label={`Head Office Share (${summary.headOfficeSharePercent}%)`}
            value={fmtMoney(summary.headOfficeShare)}
          />
          <HoSummaryCard icon={CheckCircle2} tone="purple" label="Payments Sent" value={fmtMoney(summary.paymentsSent)} />
          <HoSummaryCard
            icon={AlertCircle}
            tone={summary.balance > 0 ? 'red' : 'green'}
            label="Balance to Pay"
            value={fmtMoney(summary.balance)}
          />
        </div>
      )}

      <DataTable
        columns={columns}
        result={data}
        state={dt}
        loading={isFetching}
        emptyMessage="No entries yet."
        toolbar={
          showSchoolPicker && (
            <Select value={schoolFilter} onChange={(e) => setSchoolFilter(e.target.value)} className="sm:w-56">
              <option value="">All schools</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          )
        }
      />

      <AddEntryModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={invalidate}
        schools={showSchoolPicker ? schools : undefined}
        defaultSessionYear={isSchoolScope ? effectiveSessionYear : undefined}
      />
    </>
  );
}

function HoSummaryCard({
  icon: Icon,
  tone,
  label,
  value,
}: {
  icon: typeof Banknote;
  tone: 'green' | 'blue' | 'purple' | 'red';
  label: string;
  value: string;
}) {
  const toneClass = {
    green: 'bg-green-100 text-green-700',
    blue: 'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700',
    red: 'bg-red-100 text-red-700',
  }[tone];
  return (
    <div className="card flex items-center gap-3 p-4">
      <div className={`rounded-full p-2.5 ${toneClass}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xs font-medium text-slate-500">{label}</div>
        <div className="mt-0.5 text-lg font-bold text-slate-900">{value}</div>
      </div>
    </div>
  );
}

function AddEntryModal({
  open,
  onClose,
  onSaved,
  schools,
  defaultSessionYear,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  schools?: { id: string; name: string }[];
  defaultSessionYear?: string;
}) {
  const [schoolId, setSchoolId] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [referenceNo, setReferenceNo] = useState('');
  const [sessionYear, setSessionYear] = useState('');
  const [notes, setNotes] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState<string | undefined>(undefined);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      setSchoolId('');
      setAmount('');
      setDate(new Date().toISOString().slice(0, 10));
      setPaymentMethod('Cash');
      setReferenceNo('');
      setSessionYear(defaultSessionYear ?? '');
      setNotes('');
      setAttachmentUrl(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = useMutation({
    mutationFn: (payload: CreateHoFeesSharePayload) => HoFeesShareApi.create(payload),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const valid = !!amount && Number(amount) > 0 && !!date && (!schools || !!schoolId);

  const submit = () => {
    save.mutate({
      schoolId: schools ? schoolId : undefined,
      amount: Number(amount),
      date,
      paymentMethod: paymentMethod || undefined,
      referenceNo: referenceNo.trim() || undefined,
      notes: notes.trim() || undefined,
      attachmentUrl,
      sessionYear: sessionYear.trim() || undefined,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Head Office Fees Share Entry"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={save.isPending || !valid}>
            {save.isPending ? 'Saving…' : 'Save Entry'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {schools && (
          <Field label="School" required>
            <Select value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
              <option value="">— Select school —</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Amount" required>
          <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Date" required>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label="Payment Method">
          <Select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
            <option value="Cash">Cash</option>
            <option value="Bank Transfer">Bank Transfer</option>
            <option value="Online">Online</option>
            <option value="Cheque">Cheque</option>
          </Select>
        </Field>
        <Field label="Reference #">
          <Input value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} placeholder="Optional" />
        </Field>
        <Field label="Session Year">
          <Input value={sessionYear} onChange={(e) => setSessionYear(e.target.value)} placeholder="e.g. 2026-27 (optional)" />
        </Field>
        <Field label="Notes">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <Field label="Attachment">
          <input
            type="file"
            disabled={uploading}
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setUploading(true);
              try {
                const { url } = await UploadApi.upload(f);
                setAttachmentUrl(url);
              } finally {
                setUploading(false);
              }
            }}
          />
          {uploading && <span className="ml-2 text-xs text-slate-400">Uploading…</span>}
          {attachmentUrl && !uploading && <span className="ml-2 text-xs text-green-600">Uploaded</span>}
        </Field>
        {save.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(save.error)}</div>}
      </div>
    </Modal>
  );
}
